import { FunctionComponent } from "react";

import {
  Dialog,
  DialogFooter,
  getTheme,
  mergeStyles,
  Option,
  OptionsSelectField,
  Stack,
  SubmissionForm,
  SubmissionFormButtons,
  Text,
  ToggleField
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { FormAdUserSearch } from "@components/form/fields/FormAdUserSearch";
import { EnvUser } from "@libs/api/gateways/env/env-gateway.dtos";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

interface ManageUser {
  id?: string;
  roles?: string[];
  isInactive?: boolean;
}

interface UserModalProps {
  user: EnvUser | undefined;
  onSave: (user: EnvUser, isCreate: boolean) => void;
  onDismiss: () => void;
}

const validator = new Validator<ManageUser>(validator => ({
  id: validator.string().required({ message: "User is required" }),
  roles: validator.array().required({ message: "Roles is required" })
}));

export const UserModal: FunctionComponent<UserModalProps> = ({
  user: initialUser,
  onSave,
  onDismiss
}) => {
  const theme = getTheme();

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.EnvSecurityWrite
  );

  const handleSubmit = async (user: ManageUser) => {
    onSave(
      {
        ...initialUser,
        id: user.id,
        isInactive: !!user.isInactive,
        roles: user.roles
      },
      !initialUser?.id
    );
    onDismiss?.();
  };

  const handleCancel = () => {
    onDismiss?.();
  };

  // TODO: Get this from the roles loaded in the Cache
  const roleOptions: Option[] = [
    { key: "ADMIN", text: "Administrator" },
    { key: "DEVELOPER", text: "Developer" },
    {
      key: "DEVELOPERSUPPORT",
      text: "Developer Support (add on beyond developer)"
    },
    { key: "DEVOPS", text: "DevOps" },
    { key: "PRODUCT", text: "Product" },
    { key: "SALES", text: "Sales" },
    { key: "SUPPORT", text: "Support" },
    { key: "SUPPORTLEAD", text: "Support Lead" },
    { key: "VIEWER", text: "Basic view mode" },
    { key: "ENV.USERADMIN", text: "Environment User Administrator" }
  ];

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title: "User Details",
        showCloseButton: true,
        onDismiss: handleCancel
      }}
      maxWidth={340}
      minWidth={340}
    >
      <SubmissionForm<ManageUser>
        initialValues={{
          id: initialUser?.id,
          roles: initialUser?.roles,
          isInactive: initialUser?.isInactive
        }}
        onSubmit={handleSubmit}
        validate={validator.validate}
        disabled={!hasWritePermission}
        hideButtons
      >
        {() => (
          <Stack verticalFill tokens={{ childrenGap: theme.spacing.l2 }}>
            <Stack verticalFill tokens={{ childrenGap: theme.spacing.m }}>
              <FormAdUserSearch
                name="id"
                label="User"
                data-test="user-id"
                placeholder="User Search"
                required
                onRenderField={
                  initialUser
                    ? () => <Text>{initialUser.name}</Text>
                    : undefined
                }
              />

              <OptionsSelectField
                name="roles"
                placeholder="Select Option"
                label="Roles"
                multiSelect
                hideSearchOption
                disabled={!hasWritePermission}
                options={roleOptions}
                data-test="user-roles"
              />

              {initialUser?.id && (
                <ToggleField
                  name="isInactive"
                  label="Inactive"
                  disabled={!hasWritePermission}
                  data-test="switch-inactive"
                />
              )}
            </Stack>

            <DialogFooter>
              <SubmissionFormButtons
                classNames={{
                  buttons: mergeStyles({ gap: 8, display: "flex" })
                }}
                cancelButtonProps={{ onClick: handleCancel }}
              />
            </DialogFooter>
          </Stack>
        )}
      </SubmissionForm>
    </Dialog>
  );
};
